import { Spinner, Tabs, Tab } from '@heroui/react';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router';

import useTrackers from '@/patient-app-common/hooks/useTrackers';
import useSegment from '@/hooks/useSegment';
import useSurveys from '@/patient-app-common/hooks/useSurveys';

import { PastEventList } from './messages/PastEventList';
import { TrackerList } from './trackers/TrackerList';
import { MetricTrackerList } from './metrics/MetricTrackerList';

const METRICS_EVENT = 'Health trackers viewed';
const SURVEYS_EVENT = 'Past survey list viewed';
const MESSAGES_EVENT = 'Past message list viewed';

export function ProgressTabs() {
  const { t } = useTranslation('progress');
  const { pathname } = useLocation();
  const { sendTrackEvent } = useSegment();

  const { allMetrics, isLoading: isLoadingSurveys } = useSurveys();
  const { isLoading: isLoadingEntries } = useTrackers();
  const isLoading = isLoadingEntries || isLoadingSurveys;

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'metrics';

  const setTab = (tab) => {
    setSearchParams({ tab });
  };

  const hasMetrics = useMemo(() => {
    if (!allMetrics?.length) return false;

    return !_.isEmpty(allMetrics);
  }, [allMetrics]);

  useEffect(() => {
    let event;
    const eventData = { pathname };

    switch (tab) {
      case 'metrics':
        event = METRICS_EVENT;
        break;
      case 'surveys':
        event = SURVEYS_EVENT;
        break;
      case 'messages':
        event = MESSAGES_EVENT;
        break;
      default:
    }
    sendTrackEvent(event, eventData);
  }, [tab]);

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner color="primary" size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col mx-auto w-full md:w-[600px] pt-4">
      <Tabs
        className="py-8"
        color="primary"
        defaultSelectedKey={hasMetrics ? 'metrics' : 'surveys'}
        variant="bordered"
        onSelectionChange={setTab}
      >
        {hasMetrics && (
          <Tab
            key="metrics"
            title={
              <div className="flex items-center space-x-2">
                <Icon icon="tabler:heartbeat" />
                <span>{t('progresstabs.tab.metrics')}</span>
              </div>
            }
          >
            <MetricTrackerList metrics={allMetrics} />
          </Tab>
        )}
        <Tab
          key="surveys"
          title={
            <div className="flex items-center space-x-2">
              <Icon icon="mdi:history" />
              <span>{t('common.link.past_surveys')}</span>
            </div>
          }
        >
          <TrackerList />
        </Tab>
        <Tab
          key="messages"
          title={
            <div className="flex items-center space-x-2">
              <Icon icon="mdi:archive-outline" />
              <span>{t('pasteventlist.title')}</span>
            </div>
          }
        >
          <PastEventList />
        </Tab>
      </Tabs>
    </div>
  );
}
